<script setup>
/* global axios */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { FUSO_ESCRITORIO } from 'dashboard/helper/fusoEscritorio';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const { t } = useI18n();
const currentChat = useMapGetter('getSelectedChat');
const accountId = useMapGetter('getCurrentAccountId');

const summary = ref('');
const generatedAt = ref(null);
const loading = ref(false);
const error = ref('');

// 🔴 O RESUMO SUMIA COM A PRÓXIMA MENSAGEM (16/09/2026).
// O observador olhava a conversa inteira: qualquer mensagem nova trocava o
// objeto, e ele regravava `summary` com o valor que a conversa trazia — o
// ANTIGO, porque gerar não atualiza a conversa guardada. Agora ele só age
// quando muda a CONVERSA ou quando chega um resumo mais novo do servidor.
watch(
  () => [
    currentChat.value?.id,
    currentChat.value?.summary_generated_at,
    currentChat.value?.summary,
  ],
  ([id, geradoEm, texto], anterior) => {
    const trocouDeConversa = !anterior || anterior[0] !== id;
    if (trocouDeConversa) {
      summary.value = texto || '';
      generatedAt.value = geradoEm || null;
      error.value = '';
      return;
    }
    if (geradoEm && geradoEm !== generatedAt.value && texto) {
      summary.value = texto;
      generatedAt.value = geradoEm;
    }
  },
  { immediate: true }
);

const generate = async () => {
  if (loading.value) return;
  loading.value = true;
  error.value = '';
  const conversaPedida = props.conversationId;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversaPedida}/summary`
    );
    // A pessoa pode ter trocado de conversa enquanto a IA escrevia.
    if (conversaPedida !== props.conversationId) return;
    summary.value = data.summary;
    generatedAt.value = data.generated_at;
  } catch (err) {
    if (conversaPedida !== props.conversationId) return;
    error.value = summary.value
      ? t('AI_SUMMARY.REFRESH_FAILED')
      : err?.response?.data?.error || t('AI_SUMMARY.ERROR_DEFAULT');
  } finally {
    loading.value = false;
  }
};

const dataGerada = computed(() => {
  if (!generatedAt.value) return '';
  return new Date(generatedAt.value).toLocaleString('pt-BR', {
    timeZone: FUSO_ESCRITORIO,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
});
</script>

<template>
  <!-- Íris: no produto inteiro azul é CONVERSA e íris é IA — a mesma cor do
       copiloto e do "Resumir com IA" das Publicações. -->
  <section class="ais" :class="{ 'ais--gerando': loading }" aria-live="polite">
    <header class="ais__topo">
      <span class="ais__selo" aria-hidden="true">
        <i class="i-lucide-sparkles" />
      </span>
      <p class="advup-rotulo advup-rotulo--secao ais__tit">
        {{ t('AI_SUMMARY.TITLE') }}
      </p>
      <button
        v-if="summary"
        v-tooltip.top="t('AI_SUMMARY.REFRESH')"
        type="button"
        class="advup-acao advup-acao--p advup-acao--tingida advup-acao--t-iris"
        :aria-label="t('AI_SUMMARY.REFRESH')"
        :disabled="loading"
        @click="generate"
      >
        <i
          :class="
            loading ? 'i-lucide-loader-2 advup-gira' : 'i-lucide-refresh-cw'
          "
        />
      </button>
    </header>

    <div v-if="summary" class="ais__corpo">
      <p class="ais__texto">{{ summary }}</p>
      <p v-if="error" class="advup-nota advup-nota--erro ais__aviso">
        <span class="ais__aviso-ico" aria-hidden="true">
          <i class="i-lucide-circle-alert" />
        </span>
        {{ error }}
      </p>
      <p v-else-if="dataGerada" class="advup-nota ais__quando">
        {{ t('AI_SUMMARY.GENERATED_AT', { date: dataGerada }) }}
      </p>
    </div>

    <div v-else-if="loading" class="ais__corpo ais__gerando">
      <span class="ais__linha" />
      <span class="ais__linha" />
      <span class="ais__linha ais__linha--curta" />
      <p class="advup-nota">{{ t('AI_SUMMARY.GENERATING') }}</p>
    </div>

    <div v-else class="ais__corpo ais__vazio">
      <p class="advup-nota">{{ t('AI_SUMMARY.EMPTY_HINT') }}</p>
      <p v-if="error" class="advup-nota advup-nota--erro ais__aviso">
        <span class="ais__aviso-ico" aria-hidden="true">
          <i class="i-lucide-circle-alert" />
        </span>
        {{ error }}
      </p>
      <button
        type="button"
        class="advup-btn advup-btn--compacto ais__gerar"
        @click="generate"
      >
        <i class="i-lucide-sparkles" aria-hidden="true" />
        {{ t('AI_SUMMARY.GENERATE') }}
      </button>
    </div>
  </section>
</template>

<style scoped>
/* O cartão do resumo. Véu íris claro e fio íris: separa o que a IA escreveu
   do que é dado do contato, sem gritar — é uma sugestão, não um aviso. */
.ais {
  overflow: hidden;
  border: 1px solid rgb(var(--iris-5));
  border-radius: var(--r-4);
  background: linear-gradient(
    160deg,
    rgb(var(--iris-2)) 0%,
    rgb(var(--solid-1)) 70%
  );
}

.dark .ais {
  border-color: rgb(var(--iris-6));
  background: linear-gradient(
    160deg,
    rgb(var(--iris-3)) 0%,
    rgb(var(--surface-2)) 75%
  );
}

.ais__topo {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  padding: var(--esp-2) var(--esp-2) var(--esp-2) var(--esp-3);
}

.ais__selo {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: var(--r-2);
  background: linear-gradient(
    150deg,
    rgb(var(--iris-9)) 0%,
    rgb(var(--violet-9)) 120%
  );
  color: #fff;
  box-shadow: 0 4px 10px -4px rgb(var(--iris-9) / 70%);
}

.ais__selo > i {
  display: block;
  font-size: var(--ico-1);
  line-height: 1;
}

/* A estrela brilha enquanto a IA escreve: o único movimento do cartão, e
   ele diz exatamente o que está acontecendo. */
.ais--gerando .ais__selo > i {
  animation: ais-brilha calc(var(--mov-4) * 3) var(--mov-curva) infinite;
}

@keyframes ais-brilha {
  50% {
    opacity: 0.45;
    transform: scale(0.82) rotate(12deg);
  }
}

.ais__tit {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: rgb(var(--iris-11));
}

.ais__corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  padding: 0 var(--esp-3) var(--esp-3);
}

.ais__texto {
  margin: 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-12));
  white-space: pre-wrap;
  animation: advupPop var(--mov-3) var(--mov-curva) backwards;
}

.ais__quando {
  margin: 0;
  color: rgb(var(--slate-10));
}

.ais__aviso {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-1);
  margin: 0;
}

.ais__aviso-ico {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  /* Uma linha de altura: o ícone fica no meio da PRIMEIRA linha do aviso,
     mesmo quando ele quebra. */
  height: 1lh;
  font-size: var(--ico-1);
}

.ais__vazio {
  align-items: flex-start;
}

.ais__vazio > .advup-nota {
  margin: 0;
}

.ais__gerar {
  color: rgb(var(--iris-11));
}

/* O esqueleto do texto que vem: três linhas que respiram no tom da IA. */
.ais__linha {
  display: block;
  height: 8px;
  border-radius: var(--r-redondo);
  background: rgb(var(--iris-4));
  animation: ais-respira calc(var(--mov-4) * 3) var(--mov-curva) infinite;
}

.ais__linha:nth-child(2) {
  animation-delay: var(--mov-2);
}

.ais__linha--curta {
  width: 60%;
  animation-delay: var(--mov-3);
}

.ais__gerando > .advup-nota {
  margin: var(--esp-1) 0 0;
}

@keyframes ais-respira {
  50% {
    opacity: 0.4;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ais--gerando .ais__selo > i,
  .ais__linha,
  .ais__texto {
    animation: none;
  }
}
</style>
